import { TFunction } from "react-i18next";
import { OptionItem } from "../../types";
import { toJSDate } from "../Persons/HelperFunctions";
import {
  OptionType as PersonOptionType,
  mapGenderToOption,
} from "../Persons/Person";
import { PersonCustomerRelationship } from "../Persons/PersonForm";

export interface User {
  id: string;
  email: string;
  password: string;
  displayedLoginName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  repeatPassword: string;
  phone: string;
  active: boolean;
  userDemographic?: {
    email?: string;
  };
  customer?: {
    id: string;
    customerDomainName: string;
  };
  person: {
    id?: string;
    relationshipToCustomer?: string | PersonOptionType;
    primaryLocation?: PersonOptionType;
    yearsInJob?: string;
    personSensitive: {
      gender?: string;
      birthDate?: string | Date;
      streetAddress?: string;
      address2?: string;
      city?: string;
      zipPostal?: string;
      stateProv?: string;
      country?: string;
      currentPosition?: string;
      currentPositionStartDate?: Date;
    };
  };
}

export interface UserForm {
  id: string;
  email: string;
  password: string;
  displayedLoginName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  repeatPassword: string;
  phone: string;
  active: boolean;
  person: {
    id?: string;
    relationshipToCustomer?: string | PersonOptionType;
    primaryLocation?: PersonOptionType;
  };
  gender?: string | OptionType;
  birthDate?: string | Date;
  streetAddress?: string;
  address2?: string;
  city?: string;
  zipPostal?: string;
  stateProv?: string;
  country?: string;
  currentPositionStartDate?: Date | string;
  currentPosition?: string;
  yearsInJob?: string;
}

export interface UserRequest {
  id?: string;
  userID?: string;
  email: string;
  password: string;
  displayedLoginName: string;
  firstName: string;
  lastName: string;
  newPassword?: string;
  repeatPassword?: string;
  phone: string;
  active: boolean;
}

export interface UserDemographic {
  id: string;
  password?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: string;
  displayName?: string;
}

export const defaultUser: User = {
  email: "",
  password: "",
  displayedLoginName: "",
  firstName: "",
  lastName: "",
  id: "",
  newPassword: "",
  repeatPassword: "",
  phone: "",
  active: true,
  person: { personSensitive: {} },
};

export type OptionType = {
  label: string;
  value: string;
};

export enum UserStatus {
  Active = "Active",
  Inactive = "Inactive",
}

export const UserStatusOptions = Object.values(UserStatus).map((value) => ({
  id: value,
  label: value,
}));

export const userStatusOptions = (
  t: TFunction<"translation", undefined>
): OptionItem<number>[] => {
  return [
    { id: "active", label: t("status_active"), value: 1 },
    { id: "inactive", label: t("status_inactive"), value: 0 },
  ];
};

export const userStatusOptionDisplay = (rowData: any) => {
  if (!rowData) return "No data available";
  return rowData.label;
};

export const mapUserToFormData = (user: User, treeData: any) => {
  const relationshipToCustomer =
    typeof user.person?.relationshipToCustomer === "string"
      ? PersonCustomerRelationship().find(
          (option) => option.value === user.person.relationshipToCustomer
        )
      : user.person?.relationshipToCustomer;

  const primaryLocation =
    typeof user.person?.primaryLocation === "string"
      ? treeData?.treeStore.active.flatTree.find(
          (location) => location.id === user.person.primaryLocation
        )
      : user.person?.primaryLocation;

  const birthDate =
    typeof user.person?.personSensitive?.birthDate === "string"
      ? toJSDate(user.person.personSensitive.birthDate)
      : "";

  const currentPositionStartDate =
    typeof user.person?.personSensitive?.currentPositionStartDate === "string"
      ? toJSDate(user.person.personSensitive.currentPositionStartDate)
      : "";

  // User
  const gender = mapGenderToOption(user?.person.personSensitive?.gender);

  return {
    ...user,
    person: {
      primaryLocation,
      relationshipToCustomer,
    },
    birthDate: birthDate,
    currentPositionStartDate: currentPositionStartDate,
    yearsInJob: user.person?.yearsInJob,
    gender: gender,
  };
};
